import { forwardRef, memo, useImperativeHandle } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { useSKU } from 'hooks/useSKU';
import { useAppSelector } from 'store/hooks';
import { washhubSelector } from 'store/slices/washhub';
import { useMutation, useQueryClient } from 'react-query';
import { successToast } from 'utils/successToast';
import {
  RefType,
  AddWashhubInventoryModalProps,
} from './AddWashhubInventoryModal.props';
import AddWashhubInventoryModalView from './AddWashhubInventoryModal.view';
import { washHub } from 'store/slices';
import { toApiPayloadDateFormat } from 'utils/dateUtils';
import { addHubInventoryCount } from 'api/washhub';
import { WashhubInventory } from 'types/washhub';

const Container = forwardRef<RefType, AddWashhubInventoryModalProps>(
  (_, ref) => {
    const queryClient = useQueryClient();
    const washHubId = washHub.selectors.useSelectedWashHubId();
    const washHubs = useAppSelector(washhubSelector);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { mutate, isLoading } = useMutation(addHubInventoryCount);

    const { data: skus = [], isLoading: isLoadingSKU } = useSKU(
      'package',
      washHubId,
    );

    // Equipment is not assigned to hubs, and the hub filter only matches the
    // hub's package SKUs, so it has to be skipped or nothing comes back.
    const { data: equipmentSkus = [], isLoading: isLoadingEquipment } = useSKU(
      'equipment',
      undefined,
      undefined,
      undefined,
      true,
    );

    useImperativeHandle(
      ref,
      () => ({
        onOpen,
      }),
      [onOpen],
    );

    const onSubmit = (values: WashhubInventory) => {
      values.date = toApiPayloadDateFormat(values.date);
      mutate(values, {
        onSuccess: async () => {
          successToast('Hub Inventory Count created successfully');

          onClose();

          queryClient.invalidateQueries({
            queryKey: ['fetch-hub-inventory-count'],
          });
        },
      });
    };
    const _props = {
      skus,
      equipmentSkus,
      isOpen,
      onClose,
      onSubmit,
      washHubs,
      isLoading: isLoadingSKU || isLoadingEquipment || isLoading,
      washHub: washHubId,
    };
    return <AddWashhubInventoryModalView {..._props} />;
  },
);

const AddWashhubInventoryModal = memo(Container);

export default AddWashhubInventoryModal;
